import NavigationTypes from './navigation-types.js';
const { NAVIGATION, PARAMETER } = NavigationTypes;
const {
    SPORT_ID,
    LOTTERY_ID,
    EVENT_ID,
    CLASS_ID,
    TYPE_ID,
    COUPON_ID,
    CATEGORY,
    MEETING_ID,
    RACE_ID,
    URL,
    FILTER,
    ITEM_ID,
    PART_ID,
} = PARAMETER;

/**
 * Helper class offers functionality to build navigational payload objects and dynamic URL paths.
 *
 * @class NavigationPathBuilder
 * @memberof Mojito.Presentation.Base.Navigation
 */

/**
 * Builds object used to navigate to sport page.
 *
 * @function toSport
 *
 * @param {string} sportId - Sport id.
 * @returns {Mojito.Presentation.Base.Navigation.types.NavigationPayload} Navigation payload object.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const toSport = (sportId = '') => ({
    type: NAVIGATION.SPORT_PAGE,
    params: { [SPORT_ID]: sportId },
});

/**
 * Builds object used to navigate to sport event page.
 *
 * @function toSportEvent
 *
 * @param {string} sportId - Sport id.
 * @param {string} eventId - Event id.
 * @returns {Mojito.Presentation.Base.Navigation.types.NavigationPayload} Navigation payload object.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const toSportEvent = (sportId, eventId) => ({
    type: NAVIGATION.SPORT_EVENT_PAGE,
    params: { [SPORT_ID]: sportId || '', [EVENT_ID]: eventId || '' },
});

/**
 * Builds object used to navigate to inplay events page.
 * Accepts optional sportId parameter to perform navigation to inplay events of desired sport.
 *
 * @function toInplay
 *
 * @param {string} [sportId] - Sport id.
 * @returns {Mojito.Presentation.Base.Navigation.types.NavigationPayload} Navigation payload object.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const toInplay = sportId =>
    sportId
        ? {
              type: NAVIGATION.INPLAY_SPORT_PAGE,
              params: { sportId: sportId },
          }
        : {
              type: NAVIGATION.INPLAY_PAGE,
              params: {},
          };

/**
 * Builds object used to navigate to home page.
 *
 * @function toHome
 *
 * @returns {Mojito.Presentation.Base.Navigation.types.NavigationPayload} Navigation payload object.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const toHome = () => ({ type: NAVIGATION.HOME });

/**
 * Builds a navigation payload object used for navigating to a betslip deeplink.
 *
 * @function toBetslipDeepLink
 *
 * @returns {Mojito.Presentation.Base.Navigation.types.NavigationPayload} Navigation payload object.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const toBetslipDeepLink = () => ({
    type: NAVIGATION.DEEP_LINKS_ADD_SELECTIONS,
});

/**
 * Builds object used to navigate to quick start guide sub page within home page.
 *
 * @function toQuickStartGuide
 *
 * @returns {Mojito.Presentation.Base.Navigation.types.NavigationPayload} Navigation payload object.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const toQuickStartGuide = () => ({
    ...toHome(),
    nested: {
        type: NAVIGATION.QUICK_START_GUIDE,
    },
});

/**
 * Builds object used to navigate to inplay event.
 *
 * @function toInplayEvent
 *
 * @param {string} eventId - Event id.
 * @returns {Mojito.Presentation.Base.Navigation.types.NavigationPayload} Navigation payload object.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const toInplayEvent = eventId => ({
    type: NAVIGATION.INPLAY_EVENT_PAGE,
    params: { [EVENT_ID]: eventId || '' },
});

/**
 * Builds object used to navigate to sport coupon.
 * For example, today, tomorrow, price boost, cms coupons, etc.
 *
 * @function toSportCoupon
 *
 * @param {string} sportId -Sport id.
 * @param {string} couponId - Coupon id.
 * @returns {Mojito.Presentation.Base.Navigation.types.NavigationPayload} Navigation payload object.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const toSportCoupon = (sportId, couponId) => ({
    type: NAVIGATION.SPORTS_COUPON_PAGE,
    params: { [SPORT_ID]: sportId || '', [COUPON_ID]: couponId || '' },
});

/**
 * Builds object used to navigate to sport highlights.
 *
 * @function toSportHighlights
 *
 * @param {string} sportId - Sport id.
 * @param {string} sportClassId - Sport class id.
 * @returns {Mojito.Presentation.Base.Navigation.types.NavigationPayload} Navigation payload object.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const toSportHighlights = (sportId, sportClassId) => ({
    ...toSport(sportId),
    nested: {
        type: NAVIGATION.HIGHLIGHTS,
        anchor: sportClassId,
    },
});

/**
 * Builds object used to navigate to future racing sport pivot.
 *
 * @function toSportFuture
 *
 * @param {string} sportId - Sport id.
 * @returns {Mojito.Presentation.Base.Navigation.types.NavigationPayload} Navigation payload object.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const toSportFuture = sportId => ({
    ...toSport(sportId),
    nested: {
        type: NAVIGATION.FUTURE,
    },
});

/**
 * Builds object used to navigate to sport matches.
 *
 * @function toSportMatches
 *
 * @param {string} sportId - Sport id value to build correct path `/sports/:sportId/matches/:filter`.
 * @param {string} [filter] - Value of the filter to show the correct event coupon `/sports/:sportId/matches/:filter`.
 * @returns {Mojito.Presentation.Base.Navigation.types.NavigationPayload} Navigation payload object.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const toSportMatches = (sportId, filter) => ({
    ...toSport(sportId),
    nested: {
        type: NAVIGATION.MATCHES,
        params: filter ? { [FILTER]: filter } : undefined,
    },
});

/**
 * Builds object used to navigate to sport priceboosts.
 *
 * @function toSportPriceBoosts
 *
 * @param {string} sportId - Sport id.
 * @returns {Mojito.Presentation.Base.Navigation.types.NavigationPayload} Navigation payload object.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const toSportPriceBoosts = sportId => ({
    ...toSport(sportId),
    nested: {
        type: NAVIGATION.PRICE_BOOSTS,
    },
});

/**
 * Builds object used to navigate to sport prebuilt bet builder bets.
 *
 * @function toSportPreBetBuilders
 *
 * @param {string} sportId - Sport id.
 * @returns {Mojito.Presentation.Base.Navigation.types.NavigationPayload} Navigation payload object.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const toSportPreBetBuilders = sportId => ({
    ...toSport(sportId),
    nested: {
        type: NAVIGATION.PRE_BET_BUILDERS,
    },
});

/**
 * Builds object used to navigate to sport competitions.
 *
 * @function toSportCompetitions
 *
 * @param {string} sportId - Sport id.
 * @param {string} sportClassId - Sport class id.
 * @returns {Mojito.Presentation.Base.Navigation.types.NavigationPayload} Navigation payload object.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const toSportCompetitions = (sportId, sportClassId) => ({
    ...toSport(sportId),
    nested: {
        type: NAVIGATION.COMPETITIONS,
        anchor: sportClassId,
    },
});

/**
 * Builds object used to navigate to sport competitions league.
 *
 * @function toSportCompetitionsLeague
 *
 * @param {string} sportId - Sport id.
 * @param {string} typeId - Type id.
 * @returns {Mojito.Presentation.Base.Navigation.types.NavigationPayload} Navigation payload object.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */

const toSportCompetitionsLeague = (sportId, typeId) => ({
    type: NAVIGATION.COMPETITIONS_LEAGUE,
    params: {
        [SPORT_ID]: sportId,
        [TYPE_ID]: typeId,
    },
});

/**
 * Builds object used to navigate to sport matches league.
 *
 * @function toSportCompetitionsLeague
 *
 * @param {string} sportId - Sport id.
 * @param {string} typeId - Type id.
 * @returns {Mojito.Presentation.Base.Navigation.types.NavigationPayload} Navigation payload object.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const toSportMatchesLeague = (sportId, typeId) => ({
    ...toSportCompetitionsLeague(sportId, typeId),
    nested: {
        type: NAVIGATION.MATCHES_LEAGUE,
    },
});

/**
 * Builds object used to navigate to sport price boosts league.
 *
 * @function toSportPriceBoostsLeague
 *
 * @param {string} sportId - Sport id.
 * @param {string} typeId - Type id.
 * @returns {Mojito.Presentation.Base.Navigation.types.NavigationPayload} Navigation payload object.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const toSportPriceBoostsLeague = (sportId, typeId) => ({
    ...toSportCompetitionsLeague(sportId, typeId),
    nested: {
        type: NAVIGATION.PRICE_BOOSTS_LEAGUE,
    },
});

/**
 * Builds object used to navigate to sport prebuilt bet builders league.
 *
 * @function toSportPreBetBuilders
 *
 * @param {string} sportId - Sport id.
 * @param {string} typeId - Type id.
 * @returns {Mojito.Presentation.Base.Navigation.types.NavigationPayload} Navigation payload object.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const toSportPreBetBuildersLeague = (sportId, typeId) => ({
    ...toSportCompetitionsLeague(sportId, typeId),
    nested: {
        type: NAVIGATION.BET_BUILDERS_LEAGUE,
    },
});

/**
 * Builds object used to navigate to a league standings table.
 *
 * @function toSportTableLeague
 *
 * @param {string} sportId - Sport id.
 * @param {string} typeId - Type id.
 * @returns {Mojito.Presentation.Base.Navigation.types.NavigationPayload} Navigation payload object.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const toSportTableLeague = (sportId, typeId) => ({
    ...toSportCompetitionsLeague(sportId, typeId),
    nested: {
        type: NAVIGATION.TABLE_LEAGUE,
    },
});

/**
 * Builds object used to navigate to sport outrights league.
 *
 * @function toSportOutrightsLeague
 *
 * @param {string} sportId - Sport id.
 * @param {string} typeId - Type id.
 * @returns {Mojito.Presentation.Base.Navigation.types.NavigationPayload} Navigation payload object.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const toSportOutrightsLeague = (sportId, typeId) => ({
    type: NAVIGATION.OUTRIGHTS_LEAGUE,
    params: {
        [SPORT_ID]: sportId,
        [TYPE_ID]: typeId,
    },
});

/**
 * Builds object used to navigate to sport outrights.
 *
 * @function toSportOutright
 *
 * @param {string} sportId - Sport id.
 * @param {string} sportClassId - Sport class id.
 * @returns {Mojito.Presentation.Base.Navigation.types.NavigationPayload} Navigation payload object.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const toSportOutright = (sportId, sportClassId) => ({
    ...toSport(sportId),
    nested: {
        type: NAVIGATION.OUTRIGHTS,
        anchor: sportClassId,
    },
});

/**
 * Builds object used to navigate to sport category.
 * For example, highlights, competitions, outrights, meetings, future races, etc.
 *
 * @function toSportCategory
 *
 * @param {string} sportId - Sport id.
 * @param {string} category - Category name.
 * @returns {Mojito.Presentation.Base.Navigation.types.NavigationPayload} Navigation payload object.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const toSportCategory = (sportId, category) => ({
    type: NAVIGATION.SPORT_CATEGORY_PAGE,
    params: { [SPORT_ID]: sportId || '', [CATEGORY]: category || '' },
});

/**
 * Builds object used to navigate to racing sport meetings.
 *
 * @function toSportMeetings
 *
 * @param {string} sportId - The id of the racing sport.
 * @param {string} filter - The selected content filter of the racing sport (TODAY,TOMORROW,STREAMING).
 *
 * @returns {Mojito.Presentation.Base.Navigation.types.NavigationPayload} Navigation payload object.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const toSportMeetings = (sportId, filter) => ({
    ...toSport(sportId),
    nested: {
        type: NAVIGATION.MEETINGS,
        params: filter ? { [FILTER]: filter } : undefined,
    },
});

/**
 * Builds object used to navigate to the first race event available for the specified meeting id.
 *
 * @function toRaceMeeting
 *
 * @param {string} sportId - The id of the racing sport.
 * @param {string} meetingId - Meeting id.
 * @returns {Mojito.Presentation.Base.Navigation.types.NavigationPayload} Navigation payload object.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const toRaceMeeting = (sportId, meetingId) => ({
    type: NAVIGATION.RACE_MEETING_PAGE,
    params: { [SPORT_ID]: sportId || '', [MEETING_ID]: meetingId || '' },
});

/**
 * Builds object used to navigate to race event.
 *
 * @function toRaceEvent
 *
 * @param {string} sportId - The id of the racing sport.
 * @param {string} meetingId - Meeting id.
 * @param {string} raceId - Race event id.
 * @returns {Mojito.Presentation.Base.Navigation.types.NavigationPayload} Navigation payload object.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const toRaceEvent = (sportId, meetingId, raceId) => ({
    type: NAVIGATION.RACE_PAGE,
    params: { [SPORT_ID]: sportId || '', [MEETING_ID]: meetingId || '', [RACE_ID]: raceId || '' },
});

/**
 * Builds object used to navigate to future race event.
 *
 * @function toFutureRaceEvent
 *
 * @param {string} sportId - The id of the racing sport.
 * @param {string} raceId - Future race event id.
 * @returns {Mojito.Presentation.Base.Navigation.types.NavigationPayload} Navigation payload object.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const toFutureRaceEvent = (sportId, raceId) => ({
    type: NAVIGATION.FUTURE_RACE_PAGE,
    params: { [SPORT_ID]: sportId || '', [RACE_ID]: raceId || '' },
});

/**
 * Builds object used to navigate to virtual sports.
 *
 * @function toVirtualSports
 *
 * @returns {Mojito.Presentation.Base.Navigation.types.NavigationPayload} Navigation payload object.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const toVirtualSports = () => ({ type: NAVIGATION.VIRTUAL_SPORT_PAGE });

/**
 * Builds object used to navigate to specific virtual sport.
 *
 * @function toVirtualSport
 *
 * @param {string} sportId - The id of the virtual sport.
 * @param {string} sportClassId - The id of the virtual sport class.
 * @returns {Mojito.Presentation.Base.Navigation.types.NavigationPayload} Navigation payload object.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const toVirtualSport = (sportId, sportClassId) => ({
    type: NAVIGATION.VIRTUAL_SPORT_PAGE,
    params: { [SPORT_ID]: sportId, [CLASS_ID]: sportClassId },
});

/**
 * Builds object used to navigate to specific virtual sport event.
 *
 * @function toVirtualSportEvent
 *
 * @param {string} sportId - The id of the virtual sport.
 * @param {string} sportClassId - The id of the virtual sport class.
 * @param {string} eventId - Virtual sport event id.
 * @returns {Mojito.Presentation.Base.Navigation.types.NavigationPayload} Navigation payload object.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const toVirtualSportEvent = (sportId, sportClassId, eventId) => ({
    type: NAVIGATION.VIRTUAL_EVENT_PAGE,
    params: {
        [SPORT_ID]: sportId,
        [CLASS_ID]: sportClassId,
        [EVENT_ID]: eventId,
    },
});

/**
 * Builds object used to navigate to user account.
 *
 * @function toAccount
 *
 * @param {string} [itemId] - Account page id.
 * @param {string} [partId] - Account page part id. Usually sub page.
 * @returns {Mojito.Presentation.Base.Navigation.types.NavigationPayload} Navigation payload object.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const toAccount = (itemId, partId) => ({
    type: NAVIGATION.ACCOUNT_PAGE,
    params: {
        [ITEM_ID]: itemId,
        [PART_ID]: partId,
    },
});

/**
 * Builds object used to navigate the coupon with boosted prices events.
 *
 * @function toPriceBoost
 *
 * @returns {Mojito.Presentation.Base.Navigation.types.NavigationPayload} Navigation payload object.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const toPriceBoost = () => ({ type: NAVIGATION.PRICE_BOOST_PAGE });

/**
 * Builds object used to navigate to bad route page.
 * Usually shown when route did not match to any of the page.
 *
 * @function toBadRequest
 *
 * @returns {Mojito.Presentation.Base.Navigation.types.NavigationPayload} Navigation payload object.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const toBadRequest = () => ({ type: NAVIGATION.BAD_REQUEST_PAGE });

/**
 * Builds object used to navigate to custom page by direct url path.
 *
 * @function toCustom
 *
 * @param {string} url - URL path to be explicitly used for navigation (e.g., `/my-custom-page/item/1?param=foo` etc.).
 * @returns {Mojito.Presentation.Base.Navigation.types.NavigationPayload} Navigation payload object.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const toCustom = url => ({ type: NAVIGATION.CUSTOM_NAVIGATION, params: { [URL]: url } });

/**
 * Builds ready to use url path for sport page navigation.
 * For example, `/sports/tennis` for <code>sportId: 'tennis'</code>.
 *
 * @function sportPath
 *
 * @param {string} sportId - Sport id.
 * @param {Mojito.Core.Base.AbstractRouteResolver} routeResolver - Route resolver instance.
 * @returns {string} URL path.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const sportPath = (sportId, routeResolver) => routeResolver.getRoute(toSport(sportId));

/**
 * Builds ready to use url path for sport competitions page navigation.
 * For example, `/sports/tennis/competitions/12345` for <code>sportId: 'tennis', typeId: '12345'</code>.
 *
 * @function sportCompetitionsPath
 *
 * @param {string} sportId - Sport id.
 * @param {string} typeId - Type id.
 * @param {Mojito.Core.Base.AbstractRouteResolver} routeResolver - Route resolver instance.
 * @returns {string} URL path.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const sportCompetitionsPath = (sportId, typeId, routeResolver) =>
    routeResolver.getRoute(toSportCompetitionsLeague(sportId, typeId));

/**
 * Builds ready to use url path for sport coupon page navigation.
 * For example, `/sports/tennis/coupons/12345` for <code>sportId: 'tennis', couponId: '12345'</code>.
 *
 * @function sportCouponPath
 *
 * @param {string} sportId - Sport id.
 * @param {string} couponId - Coupon id.
 * @param {Mojito.Core.Base.AbstractRouteResolver} routeResolver - Route resolver instance.
 * @returns {string} URL path.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const sportCouponPath = (sportId, couponId, routeResolver) =>
    routeResolver.getRoute(toSportCoupon(sportId, couponId));

/**
 * Builds ready to use url path for event page navigation.
 * For example, `/sports/tennis/events/12345` for <code>sportId: 'tennis', eventId: '12345'</code>.
 *
 * @function sportEventPath
 *
 * @param {string} sportId - Sport id.
 * @param {string} eventId - Event id.
 * @param {Mojito.Core.Base.AbstractRouteResolver} routeResolver - Route resolver instance.
 * @returns {string} URL path.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const sportEventPath = (sportId, eventId, routeResolver) =>
    routeResolver.getRoute(toSportEvent(sportId, eventId));

/**
 * Builds ready to use url path for race meeting page navigation.
 * For example, `/sports/horse_racing/meetings/123/races/456` for <code>sportId: 'horse_racing', eventId: '456', meetingId: '123'</code>.
 *
 * @function raceMeetingPath
 *
 * @param {string} sportId - Sport id.
 * @param {string} eventId - Event id.
 * @param {string} meetingId - Meeting id.
 * @param {Mojito.Core.Base.AbstractRouteResolver} routeResolver - Route resolver instance.
 * @returns {string} URL path.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const raceMeetingPath = (sportId, eventId, meetingId, routeResolver) =>
    routeResolver.getRoute(toRaceEvent(sportId, meetingId, eventId));

/**
 * Builds ready to use url path for inplay events page navigation.
 * Accepts optional sportId parameter to perform navigation to inplay events of desired sport.
 * For example, `/inplay` or `/inplay/tennis` for <code>sportId: 'tennis'</code>.
 *
 * @function inplayPath
 *
 * @param {string} [sportId] - Sport id.
 * @param {Mojito.Core.Base.AbstractRouteResolver} routeResolver - Route resolver instance.
 * @returns {string} URL path.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const inplayPath = (sportId, routeResolver) => routeResolver.getRoute(toInplay(sportId));

/**
 * Builds ready to use url path for in-play event page navigation.
 * For example, `/inplay/events/12345` for <code>eventId: '12345'</code>.
 *
 * @function inplayEventPath
 *
 * @param {string} eventId - Event id.
 * @param {Mojito.Core.Base.AbstractRouteResolver} routeResolver - Route resolver instance.
 * @returns {string} URL path.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const inplayEventPath = (eventId, routeResolver) => routeResolver.getRoute(toInplayEvent(eventId));

/**
 * Builds object used to navigate to Lotto bet history page.
 *
 * @function toLottoBetHistoryPage
 *
 * @returns {Mojito.Presentation.Base.Navigation.types.NavigationPayload} Navigation payload object.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const toLottoBetHistoryPage = () => ({
    ...toHome(),
    nested: {
        type: NAVIGATION.LOTTO_BET_HISTORY_PAGE,
    },
});

/**
 * Builds object used to navigate to lottery item page.
 *
 * @function toLotteryItemPage
 *
 * @param {string} lotteryId - The id of the lottery.
 *
 * @returns {Mojito.Presentation.Base.Navigation.types.NavigationPayload} Navigation payload object.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const toLotteryItemPage = lotteryId => ({
    type: NAVIGATION.LOTTERY_ITEM_PAGE,
    params: { [LOTTERY_ID]: lotteryId },
});

/**
 * Builds object used to navigate to lotto event odds page.
 *
 * @function toLottoOddsPage
 *
 * @param {string} lottoId - Lottery id.
 * @returns {Mojito.Presentation.Base.Navigation.types.NavigationPayload} Navigation payload object.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const toLottoOddsPage = lottoId => ({
    ...toLotteryItemPage(lottoId),
    nested: {
        type: NAVIGATION.LOTTO_ODDS_PAGE,
    },
});

/**
 * Builds object used to navigate to bet history page (account sub-page).
 *
 * @function toBetHistoryPage
 *
 * @returns {Mojito.Presentation.Base.Navigation.types.NavigationPayload} Navigation payload object.
 *
 * @memberof Mojito.Presentation.Base.Navigation.NavigationPathBuilder
 */
const toBetHistoryPage = () => toAccount('bet-history');

export default {
    toBetslipDeepLink,
    toHome,
    toInplay,
    toInplayEvent,
    toSport,
    toSportEvent,
    toSportCoupon,
    toSportCompetitions,
    toSportCategory,
    toSportHighlights,
    toSportOutright,
    toRaceMeeting,
    toSportMeetings,
    toVirtualSport,
    toVirtualSports,
    toVirtualSportEvent,
    toRaceEvent,
    toFutureRaceEvent,
    toAccount,
    toPriceBoost,
    toBadRequest,
    toCustom,
    sportPath,
    sportCouponPath,
    sportEventPath,
    raceMeetingPath,
    inplayPath,
    inplayEventPath,
    sportCompetitionsPath,
    toSportMatches,
    toSportFuture,
    toSportPriceBoosts,
    toSportPreBetBuilders,
    toSportCompetitionsLeague,
    toSportOutrightsLeague,
    toSportMatchesLeague,
    toSportPriceBoostsLeague,
    toSportPreBetBuildersLeague,
    toSportTableLeague,
    toQuickStartGuide,
    toLottoBetHistoryPage,
    toLotteryItemPage,
    toLottoOddsPage,
    toBetHistoryPage,
};
